import React, { forwardRef, useRef, useState, useImperativeHandle } from "react";
import { RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import ModalComments from "src/components/ModalComments";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";

export interface IActionCommentsRef{
    openModalActionComments: (id: number, titleAction: string, onCallback?: Function) => void;
}

interface IComment {
    inb011Id: number;
    inb011Comentario: string;
    inb011Efa005Id: number;
    inb011Efa005Imagem: string;
    inb011Efa005Nome: string;
    children: IComment[];
    dateCreated: Date;
}

const CommentsModal: React.ForwardRefRenderFunction<IActionCommentsRef> = (_, ref) => {
	const { translate } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const { addToast } = RCCHookToast();
    
	// Modal comments
	const modalComment = useRef<RCCRefModalProps>(null);
	const onCallbackComments = useRef<Function | undefined>(undefined);
	const [comments, setComments] = useState<IComment[]>([]);
	const [actionId, setActionId] = useState(0);
	const [subtitle, setSubtitle] = useState<string | undefined>(undefined);

	const openModalActionComments = async (id: number, titleAction: string, onCallback?: Function) => {
		try{
			setLoading(true);

			const {data} = await RCCServicesApi.getRequest({
				method: "Actions/SelectComments",
				urlParameters: {
					id
				}
			});
			setComments(data);

			onCallbackComments.current = onCallback;
			setLoading(false);
			setActionId(id);
			setSubtitle(titleAction);
			modalComment.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar comentários"), addToast);
		}
	};

	const onAddComment = async (comment: string, commentId?: number) => {
		try{
			const {data} = await RCCServicesApi.postRequest({
				method: "Actions/InsertComment",
				body: {
					actionId,
					comment,
					commentId: commentId || null,
					actionName: subtitle
				}
			});

			addToast({
				title: translate("Sucesso"),
				description: translate("Comentário inserido com sucesso"),
				color: "green"
			});
			if(commentId){
				const newState = [...comments];
				let addedComment = false;

				const addInTree = (nodes: IComment[]) => {

					if(addedComment)
						return newState;

					if(nodes.some((node) => node.inb011Id === data.inb011Inb011Id)){
						nodes[nodes.findIndex((node) => node.inb011Id === data.inb011Inb011Id)].children.push(data);
						addedComment = true;
						return newState;
					}
									
					nodes.forEach((node) => {
						if(node.children.length > 0)
							addInTree(node.children);
					});
			
					return newState;
				};

				addInTree(newState);
				setComments(newState);
			}else{
				setComments((prevState) => [...prevState, data]);
			}

		}catch(err){
			standardErrorMessages(err, translate("Erro ao inserir comentário"), addToast);
		}
	};

	const onDeleteComment = async (id: number) => {

		await RCCServicesApi.deleteRequest({
			method: "Actions/DeleteComment",
			urlParameters: {
				actionId,
				actionName: subtitle,
				commentId: id
			}
		});

		setComments((prevState) => {
			const newState = [...prevState];
			let removedComment = false;

			const removeFromTree = (nodes: IComment[]) => {
				if(removedComment)
					return newState;

				if(nodes.some((node) => node.inb011Id === id)){
					nodes.forEach((node, index) => {
						if(node.inb011Id === id){
							nodes.splice(index, 1);
							removedComment = true;
						}
					});
					return newState;
				}
								
				nodes.forEach((node) => {
					if(node.children.length > 0)
						removeFromTree(node.children);
				});
		
				return newState;
			};
	
			removeFromTree(newState);
	
			return newState;
		});
	};

	useImperativeHandle(ref, () => ({
		openModalActionComments
	}));

	return (
		<>
			<ModalComments 
				ref={modalComment}
				defaultValue={comments}
				subtitle={`${translate("Ação")}: ${subtitle}`}
				names={{
					id: "inb011Id",
					commentary: "inb011Comentario",
					userId: "inb011Efa005Id",
					userImage: "inb011Efa005Imagem",
					userName: "inb011Efa005Nome"
				}}
				onAddComment={onAddComment}
				onDeleteComment={onDeleteComment}
				onCallbackComments={onCallbackComments.current}
			/>
		</>
	);
};

export default forwardRef(CommentsModal);
